import { faCaretDown, faMagnifyingGlass, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dialog, Transition, Combobox } from '@headlessui/react';
import { Fragment, useCallback, useEffect, useState } from 'react';

import { useOnboardingContext } from '@/contexts/onboardingContext';
import useMixpanel from '@/hooks/useTracking/useMixpanel';
import Spinner from '@/ui/components/Spinner';
import Label from '@/ui/members/components/form/Label';
import { focusClasses } from '@/ui/members/components/form/styles';

import { Props, SelectOption } from './types';

/**
 * @deprecated Do not use this component. Use the base components at the root of the UI folder instead.
 */
const SelectWithDialog = ({
  autoFocus = false,
  defaultOpen = false,
  dialogHeading,
  dialogSubHeading,
  disabled = false,
  id,
  isLoading,
  label,
  onChange,
  onSearch,
  options,
  scrollPagination,
  seeMore,
  selected,
  placeholder = 'Select an option...'
}: Props) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [visibleOptions, setVisibleOptions] = useState<SelectOption[]>(options);
  const [filteredOptions, setFilteredOptions] = useState<SelectOption[]>(visibleOptions);
  const disabledClassNames = disabled ? 'opacity-50 cursor-not-allowed' : '';
  const mixpanel = useMixpanel();
  const context = useOnboardingContext();

  const selectedOption = options.find((opt) => opt.value === selected) || null;
  useEffect(() => {
    // we can have two different behavioirs here:
    // 1. if the selected option is not visible, we can trigger the seeMore to show all invisble options
    // 2. show visible options only , but keep the selected option visible
    if (selectedOption?.visible === false) {
      // keep for #1 but remove for #2
      seeMore?.callback();
    }
    setVisibleOptions(
      // this intentionally checks against `false`, when the property is undefined, it is considered visible
      options.filter((option) => option.visible !== false || option === selectedOption)
    );
  }, [options, selectedOption, seeMore]);

  useEffect(() => {
    const localFilteredOptions =
      query === ''
        ? visibleOptions
        : visibleOptions.filter((option) =>
            option.label.toLowerCase().includes(query.toLowerCase())
          );
    setFilteredOptions(localFilteredOptions);

    if (!onSearch) return;

    if (localFilteredOptions.length === 0 || query === '') {
      onSearch(query);
    }
  }, [query, visibleOptions, onSearch]);

  const selectButtonTextColor = selected
    ? 'dark:text-uniform-gray-50 light:text-uniform-gray-900'
    : 'dark:text-uniform-gray-300 light:text-uniform-gray-400';

  const dialogPanelPaddingTop = dialogHeading ? 'pt-6' : 'pt-12';
  const dialogPanelPaddingBottom = 'pb-12';

  const onChangeWithTrack = (option: SelectOption) => {
    onChange(option.value);
    mixpanel.trackComboboxSelected(id, option, undefined, {
      version: context.version,
      feature: 'unified-onboarding'
    });
  };

  // Scroll-based pagination handler
  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLElement>) => {
      if (!scrollPagination) return;

      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      const { hasNextPage, isFetchingNextPage, fetchNextPage, threshold = 100 } = scrollPagination;

      const isNearBottom = scrollHeight - scrollTop - clientHeight < threshold;

      if (isNearBottom && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [scrollPagination]
  );

  return (
    <div className="font-roboto">
      {label && <Label htmlFor={id} text={label} />}

      <button
        aria-label={label}
        autoFocus={autoFocus}
        disabled={disabled}
        type="button"
        onClick={() => {
          setIsOpen(true);
          setQuery(''); // Reset search when opening
        }}
        className={`text-left h-12 block w-full py-2 px-3 pr-4 border-0 dark:bg-uniform-gray-700 light:bg-uniform-gray-100 ${selectButtonTextColor} ${focusClasses} text-base rounded-md ${disabledClassNames}`}
        role="combobox">
        <span className="flex items-center justify-between">
          {selectedOption?.label || placeholder}
          <FontAwesomeIcon className="text-xl" icon={faCaretDown} />
        </span>
      </button>

      <Transition show={isOpen} as={Fragment}>
        <Dialog onClose={setIsOpen} className="relative z-10 font-roboto">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <div className="fixed inset-0 bg-uniform-gray-900/90" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95">
              <Dialog.Panel
                className={`max-h-[92vh] relative w-full max-w-md transform overflow-hidden rounded-lg bg-uniform-white-base p-6 ${dialogPanelPaddingTop} ${dialogPanelPaddingBottom} shadow-xl transition-all`}>
                {dialogHeading && (
                  <Dialog.Title className="text-xl font-medium text-uniform-gray-900 mb-4 max-w-3/5">
                    {dialogHeading}
                  </Dialog.Title>
                )}

                {dialogSubHeading && (
                  <Dialog.Description className="text-sm text-uniform-gray-900 mb-4">
                    {dialogSubHeading}
                  </Dialog.Description>
                )}

                <Combobox
                  as="div"
                  aria-labelledby={id}
                  value={selectedOption}
                  onChange={(option: SelectOption | null) => {
                    if (!option) return;
                    onChangeWithTrack(option); // return string
                    setIsOpen(false);
                    setQuery('');
                  }}>
                  <div>
                    <button
                      type="button"
                      className="absolute top-4 right-4 text-gray-500 hover:text-uniform-gray-700"
                      onClick={() => setIsOpen(false)}>
                      <FontAwesomeIcon
                        className="text-xl text-uniform-gray-600"
                        icon={faTimesCircle}
                      />
                    </button>

                    <div className="relative mb-4">
                      <span className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-uniform-gray-400">
                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                      </span>

                      <Combobox.Input
                        aria-labelledby={id}
                        className="w-full pl-10 pr-3 py-2 border rounded-md bg-uniform-gray-100 text-uniform-gray-900 placeholder-uniform-gray-400"
                        onChange={(event) => setQuery(event.target.value)}
                        displayValue={(option: SelectOption) => option?.label || ''}
                        placeholder="Search..."
                      />
                    </div>

                    <Combobox.Options
                      static
                      className="h-89 max-h-[calc(100vh-200px)] overflow-y-auto bg-uniform-white-base z-10"
                      onScroll={handleScroll}>
                      {filteredOptions.length === 0 ? (
                        <div className="p-2 text-uniform-gray-500">No results.</div>
                      ) : (
                        filteredOptions.map((option) => (
                          <Combobox.Option
                            key={option.value}
                            value={option}
                            className={({ active }) =>
                              `cursor-pointer select-none px-4 py-2 ${
                                active
                                  ? 'bg-uniform-green-700 text-uniform-white-base'
                                  : 'text-uniform-gray-900'
                              }`
                            }>
                            {option.label}
                          </Combobox.Option>
                        ))
                      )}

                      {seeMore?.isVisible && (
                        <div className="absolute bottom-3">
                          <button
                            className="text-uniform-green-800 text-sm"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent click from bubbling
                              seeMore.callback();
                            }}>
                            {seeMore.text || 'See more options'}
                          </button>
                        </div>
                      )}

                      {(isLoading || scrollPagination?.isFetchingNextPage) && (
                        <Spinner className="absolute bottom-4" label="Loading options..." />
                      )}
                    </Combobox.Options>
                  </div>
                </Combobox>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default SelectWithDialog;
